import React, { useState, useMemo } from 'react';
import {
  BunkerDef,
  TilesetKey,
  TILESET_LABELS,
  getBunkersForTileset,
  bunkerDisplayName,
} from '../lib/bunkerCatalog';
import { PlacedBunkerState } from '../lib/layoutState';

interface BunkerPaletteProps {
  tileset: TilesetKey;
  onTilesetChange: (t: TilesetKey) => void;
  placedBunkers: PlacedBunkerState[];
  selectedType: string | null;
  onSelectType: (type: string | null) => void;
}

export const BunkerPalette: React.FC<BunkerPaletteProps> = ({
  tileset,
  onTilesetChange,
  placedBunkers,
  selectedType,
  onSelectType,
}) => {
  const [search, setSearch] = useState('');

  const bunkers = useMemo(() => {
    const list = getBunkersForTileset(tileset);
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(b => bunkerDisplayName(b, tileset).toLowerCase().includes(q));
  }, [tileset, search]);

  // Count of each type currently on the field
  const countByType = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of placedBunkers) {
      map[b.type] = (map[b.type] ?? 0) + 1;
    }
    return map;
  }, [placedBunkers]);

  const totalCount = placedBunkers.length;

  const handleCardClick = (bunker: BunkerDef) => {
    onSelectType(selectedType === bunker.type ? null : bunker.type);
  };

  return (
    <div className="flex flex-col h-full text-xs">
      {/* Tileset selector */}
      <div className="p-2 flex-shrink-0">
        <select
          value={tileset}
          onChange={e => onTilesetChange(e.target.value as TilesetKey)}
          className="w-full bg-[#333] rounded px-2 py-1 text-white"
        >
          {(Object.keys(TILESET_LABELS) as TilesetKey[]).map(key => (
            <option key={key} value={key}>{TILESET_LABELS[key]}</option>
          ))}
        </select>
      </div>

      {/* Search */}
      <div className="px-2 pb-2 flex-shrink-0">
        <input
          type="text"
          placeholder="Search bunkers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-[#333] rounded px-2 py-1 text-white placeholder-gray-500 outline-none"
        />
      </div>

      {/* Bunker list */}
      <div className="flex-1 overflow-y-auto px-2 flex flex-col gap-1">
        {bunkers.map(bunker => {
          const count = countByType[bunker.type] ?? 0;
          const isSelected = selectedType === bunker.type;
          const sprite = bunker.sprites?.[tileset];
          const tipW = (sprite?.widthFt ?? bunker.widthFt).toFixed(1);
          const tipD = (sprite?.depthFt ?? bunker.depthFt).toFixed(1);
          const thumbH = 24;
          // SNAKE_BEAM is a tall portrait sprite; show it rotated 90° (horizontal) in the palette.
          const sideways = bunker.type === 'SNAKE_BEAM';
          const wFt = sprite?.widthFt ?? bunker.widthFt;
          const dFt = sprite?.depthFt ?? bunker.depthFt;
          const thumbW = Math.min(36, Math.max(14, Math.round(
            thumbH * (sideways ? dFt / wFt : wFt / dFt)
          )));
          return (
            <button
              key={bunker.type}
              title={`${tipW} × ${tipD} ft`}
              onClick={() => handleCardClick(bunker)}
              className={[
                'w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-left',
                'transition-colors duration-100',
                isSelected
                  ? 'bg-[#0095da] text-white'
                  : 'bg-[#2a2a2a] hover:bg-[#383838] text-gray-200',
              ].join(' ')}
            >
              {/* Sprite thumbnail or proportional placeholder */}
              {sprite ? (
                sideways ? (
                  // Rotate portrait sprite 90° so the long axis runs horizontally
                  <div style={{ width: thumbW, height: thumbH, flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
                    <img
                      src={sprite.spriteUrl}
                      alt={bunker.displayName}
                      style={{
                        position: 'absolute', top: '50%', left: '50%',
                        width: thumbH, height: thumbW,
                        objectFit: 'contain',
                        transform: 'translate(-50%, -50%) rotate(90deg)',
                      }}
                    />
                  </div>
                ) : (
                <img
                  src={sprite.spriteUrl}
                  alt={bunker.displayName}
                  style={{ width: thumbW, height: thumbH, flexShrink: 0, objectFit: 'contain' }}
                  className="rounded-sm"
                />
                )
              ) : (
                <div
                  style={{ width: thumbW, height: thumbH, flexShrink: 0 }}
                  className={[
                    'rounded-sm border',
                    isSelected
                      ? 'bg-white/70 border-white/50'
                      : 'bg-[#0095da]/40 border-[#0095da]/60',
                  ].join(' ')}
                />
              )}
              <span className="truncate flex-1">{bunkerDisplayName(bunker, tileset)}</span>
              {count > 0 && (
                <span className={[
                  'ml-1 flex-shrink-0 text-[10px] font-bold px-1 rounded',
                  isSelected ? 'bg-white text-[#0095da]' : 'bg-[#0095da] text-white',
                ].join(' ')}>
                  ×{count}
                </span>
              )}
            </button>
          );
        })}
        {bunkers.length === 0 && search.trim() && (
          <p className="text-center opacity-40 pt-4">No bunkers match &ldquo;{search}&rdquo;</p>
        )}
      </div>

      {/* Footer: total count */}
      <div className="p-2 flex-shrink-0 border-t border-white/10 text-center opacity-50">
        {totalCount} bunker{totalCount !== 1 ? 's' : ''} placed
      </div>
    </div>
  );
};
